import { cn } from '@/utils';
import { COMMON_TEXT, FEATURE_FLAGS } from '@/constants';
import { IUserBadge, TUserRole } from '@/types';
import { TextContent, UserBadges, UserInfo, UserStats } from '@/components';
import { userProfileVariants } from './UserProfileCard.styles';
import React from 'react';
import type { UserStatsData } from '../UserStats/UserStats';

export interface UserProfileCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * User's display name
   */
  name: string;

  /**
   * User's job title or role
   */
  title: string;

  /**
   * URL to user's avatar image
   */
  imageUrl?: string;

  /**
   * Optional stats data when available
   */
  stats?: UserStatsData;

  /**
   * Optional badges data when available
   */
  badges?: IUserBadge[];

  /**
   * Whether to show badges section
   */
  showBadges?: boolean;

  /**
   * Whether to show stats section
   */
  showStats?: boolean;

  /**
   * Context of the user info, used for analytics or conditional styling
   */
  context?: TUserRole;

  /**
   * Loading state to indicate whether the user profile data is still being fetched or processed.
   */
  isLoading?: boolean;
}

/**
 * A user profile component that displays user information,
 * certification badges, and quick stats.
 */
export const UserProfileCard: React.FC<UserProfileCardProps> = ({
  badges = [],
  className,
  context = 'Sales Representative',
  imageUrl,
  isLoading = false,
  name,
  showBadges = FEATURE_FLAGS.ENABLE_FINAL_CERTIFICATION,
  showStats = false,
  stats,
  title,
  ...props
}) => {
  return (
    <div className={cn(userProfileVariants(), className)} {...props}>
      {/* Profile Title */}
      <TextContent
        isLoading={isLoading}
        label={COMMON_TEXT.PROFILE}
        level={2}
        loadingClassName="h-[2rem] w-full"
        type="headline"
      />

      {/* User Info Section */}
      <UserInfo
        context={context}
        imageUrl={imageUrl}
        isLoading={isLoading}
        name={name}
        title={title}
      />

      {/* Certification Badges */}
      {showBadges && <UserBadges badges={badges} />}

      {/* User Stats */}
      {showStats && <UserStats stats={stats} />}
    </div>
  );
};

UserProfileCard.displayName = 'UserProfileCard';

export default UserProfileCard;
